import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trophy, Film, Globe, Sparkles, ArrowRight, Star, Heart, Award, Users } from "lucide-react";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../lib/auth";

const REGIONS = ["All", "Asia", "Europe", "Africa", "North America", "South America", "Oceania"];

const _juryPhoto = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL || ""}${url}`;
};

export default function Festival() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [fests, setFests] = useState([]);
  const [active, setActive] = useState(null);
  const [films, setFilms] = useState([]);
  const [top, setTop] = useState([]);
  const [region, setRegion] = useState("All");
  const [sort, setSort] = useState("score");
  const [jury, setJury] = useState([]);

  useEffect(() => {
    api.get("/festival/festivals").then((r) => {
      setFests(r.data || []);
      setActive((r.data || [])[0] || null);
    }).catch(() => setFests([]));
    api.get("/jury/list").then((r) => setJury(r.data?.jury || [])).catch(() => setJury([]));
  }, []);

  useEffect(() => {
    if (!active) return;
    const params = new URLSearchParams({ festival_id: active.festival_id, sort });
    if (region !== "All") params.set("region", region);
    api.get(`/festival/submissions?${params}`).then((r) => setFilms(r.data || [])).catch(() => setFilms([]));
    // Fetch leaderboard separately so top-3 carry award_rank/label
    api.get(`/festival/leaderboard?festival_id=${active.festival_id}&limit=3`).then((r) => setTop(r.data || [])).catch(() => setTop([]));
  }, [active, region, sort]);

  const top3 = top.length > 0 ? top.slice(0, 3) : films.slice(0, 3);
  const rest = films.slice(3);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      {/* Hero */}
      <section className="relative pt-28 pb-12 px-6 lg:px-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/15 via-transparent to-[#E50914]/10 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-xs uppercase tracking-[0.25em] text-amber-400 font-bold">World Film Festival</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-3">
            {active?.name || "World Film Festival"}
          </h1>
          <p className="text-gray-400 max-w-2xl text-base sm:text-lg leading-relaxed">
            {active?.description || "An open circuit for filmmakers worldwide."}
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <Link
              data-testid="festival-submit-cta"
              to="/festival/submit"
              className="btn-red px-6 py-3 rounded font-semibold inline-flex items-center gap-2"
            >
              <Film className="w-4 h-4" /> Submit your film
            </Link>
            <Link
              to="/festival/dashboard"
              className="btn-glass px-6 py-3 rounded font-semibold inline-flex items-center gap-2 border border-white/15"
            >
              Filmmaker dashboard <ArrowRight className="w-4 h-4" />
            </Link>
            {user && (
              <Link
                data-testid="festival-following-cta"
                to="/festival/following"
                className="btn-glass px-6 py-3 rounded font-semibold inline-flex items-center gap-2 border border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
              >
                <Heart className="w-4 h-4" /> Following
              </Link>
            )}
            {active?.ticket_price > 0 && (
              <span className="ml-auto text-xs uppercase tracking-widest text-gray-500 self-center">
                Tickets ${active.ticket_price.toFixed(2)} per film
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Meet the Jury — credibility + attraction for filmmakers */}
      {jury.length > 0 && (
        <section className="px-6 lg:px-12 pb-10" data-testid="festival-jury-panel">
          <div className="max-w-[1400px] mx-auto">
            <div className="relative overflow-hidden rounded-2xl border border-amber-300/15 bg-gradient-to-br from-[#1a0608] via-[#100406] to-[#0A0A0A] p-6 lg:p-10">
              <div className="absolute -right-32 -top-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl pointer-events-none"
                   style={{ background: "radial-gradient(circle, #f6c34a 0%, transparent 70%)" }} />
              <div className="relative flex flex-wrap items-end justify-between gap-4 mb-6">
                <div>
                  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-amber-300 mb-2">
                    <Award className="w-4 h-4" /> Meet the Jury
                  </div>
                  <h2 className="text-2xl md:text-4xl font-black tracking-tight leading-tight">
                    The humans deciding your film&rsquo;s fate.
                  </h2>
                  <p className="text-gray-400 mt-2 max-w-2xl text-sm md:text-base leading-relaxed">
                    Directors, critics, and festival veterans watch every submission end-to-end.
                    Real experts, real scores — no algorithms, no shortcuts.
                  </p>
                </div>
                <div className="hidden md:flex items-center gap-2 text-xs text-amber-200/80 border border-amber-300/20 bg-amber-300/5 rounded-full px-3 py-1.5">
                  <Users className="w-3.5 h-3.5" /> {jury.length} juror{jury.length === 1 ? "" : "s"} · Spring 2026
                </div>
              </div>
              <div className="relative grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {jury.slice(0, 12).map((j) => {
                  const p = _juryPhoto(j.photo_url);
                  return (
                    <Link
                      key={j.user_id}
                      to={`/jury/${j.slug}`}
                      data-testid={`festival-jury-card-${j.slug}`}
                      className="group text-center block"
                    >
                      {p ? (
                        <img
                          src={p}
                          alt={j.display_name}
                          className="w-full aspect-square rounded-full object-cover border-2 border-amber-300/30 group-hover:border-amber-300 transition-colors shadow-lg"
                        />
                      ) : (
                        <div className="w-full aspect-square rounded-full bg-[#1a0608] border-2 border-amber-300/30 group-hover:border-amber-300 flex items-center justify-center text-3xl font-black text-[#E50914] shadow-lg transition-colors">
                          {(j.display_name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="mt-3 text-sm font-bold leading-tight line-clamp-2 group-hover:text-amber-200 transition-colors">
                        {j.display_name}
                      </div>
                      {j.achievements?.[0] && (
                        <div className="text-[11px] text-gray-500 mt-1 line-clamp-2 leading-snug">
                          {j.achievements[0]}
                        </div>
                      )}
                    </Link>
                  );
                })}
              </div>
              {jury.length > 12 && (
                <div className="relative text-center mt-8">
                  <span className="text-xs text-gray-500">+ {jury.length - 12} more jurors on the panel</span>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Top 3 winners podium */}
      {top3.length > 0 && (
        <section className="px-6 lg:px-12 pb-10">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center gap-2 mb-5">
              <Trophy className="w-5 h-5 text-amber-400" />
              <h2 className="text-lg font-bold uppercase tracking-widest">Live Leaderboard</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {top3.map((f, i) => (
                <Link
                  key={f.submission_id}
                  data-testid={`leaderboard-rank-${i + 1}`}
                  to={`/festival/films/${f.submission_id}`}
                  className={`group relative aspect-[3/4] rounded-lg overflow-hidden border ${
                    i === 0 ? "border-amber-400/60 ring-2 ring-amber-400/30" :
                    i === 1 ? "border-gray-300/40" :
                    "border-amber-700/40"
                  } bg-gradient-to-br from-[#1a0a0a] to-[#0a0a0a] hover:scale-[1.02] transition-transform`}
                >
                  {f.poster_url && <img src={f.poster_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                  <div className={`absolute top-3 left-3 text-7xl font-black ${i === 0 ? "text-amber-400" : i === 1 ? "text-gray-300" : "text-amber-700"} drop-shadow-2xl`}>
                    #{i + 1}
                  </div>
                  {f.award_label && (
                    <div className={`absolute top-3 right-3 px-2 py-1 rounded text-[10px] font-black uppercase tracking-widest ${
                      i === 0 ? "bg-amber-400/90 text-black" :
                      i === 1 ? "bg-gray-300/90 text-black" :
                      "bg-amber-700/90 text-white"
                    }`}>
                      🏆 {f.award_label}
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">{f.region || "—"} · {f.runtime_min || 0} min</div>
                    <h3 className="text-lg font-bold leading-tight mb-1 line-clamp-2">{f.title}</h3>
                    <div className="flex items-center gap-2 text-xs text-gray-300">
                      <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                      <span className="font-bold">{(f.score || 0).toFixed(2)}</span>
                      <span className="text-gray-500">· {f.total_votes || 0} votes</span>
                    </div>
                    {f.judged_by?.length > 0 && (
                      <div className="mt-2.5 flex items-center gap-2" data-testid={`judged-by-rank-${i + 1}`} title={`Judged by ${f.judged_by.map((j) => j.display_name).join(", ")}`}>
                        <div className="flex -space-x-2">
                          {f.judged_by.slice(0, 4).map((j) => {
                            const p = _juryPhoto(j.photo_url);
                            return p ? (
                              <img
                                key={j.user_id}
                                src={p}
                                alt={j.display_name}
                                className="w-6 h-6 rounded-full object-cover border-2 border-black"
                              />
                            ) : (
                              <div
                                key={j.user_id}
                                className="w-6 h-6 rounded-full bg-[#1a0608] border-2 border-black flex items-center justify-center text-[10px] font-black text-amber-300"
                              >
                                {(j.display_name || "?").charAt(0).toUpperCase()}
                              </div>
                            );
                          })}
                        </div>
                        <span className="text-[10px] uppercase tracking-widest text-amber-300/80">
                          Judged by {f.judged_by.length}
                        </span>
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Filters */}
      <section className="px-6 lg:px-12 pb-6">
        <div className="max-w-[1400px] mx-auto flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-gray-400">
            <Globe className="w-3.5 h-3.5" /> Region:
          </div>
          {REGIONS.map((r) => (
            <button
              key={r}
              data-testid={`region-${r.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setRegion(r)}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                region === r ? "bg-white text-black border-white" : "bg-transparent border-white/15 text-gray-400 hover:text-white hover:border-white/40"
              }`}
            >{r}</button>
          ))}
          <div className="ml-auto flex items-center gap-2 text-xs">
            <span className="text-gray-500 uppercase tracking-widest">Sort:</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-black/60 border border-white/10 rounded px-2 py-1 text-xs"
              data-testid="festival-sort-select"
            >
              <option value="score">Top rated</option>
              <option value="votes">Most votes</option>
              <option value="newest">Newest</option>
            </select>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="px-6 lg:px-12 pb-20">
        <div className="max-w-[1400px] mx-auto">
          {rest.length === 0 && films.length === 0 ? (
            <div
              data-testid="festival-empty"
              className="relative overflow-hidden rounded-xl border border-white/10 bg-gradient-to-br from-[#1a0a0a] to-[#0a0a0a] px-8 py-16 text-center"
            >
              <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
                   style={{
                     backgroundImage: "radial-gradient(circle at 25% 25%, #E50914 0%, transparent 40%), radial-gradient(circle at 75% 75%, #fbbf24 0%, transparent 40%)",
                   }} />
              <div className="relative max-w-md mx-auto">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#fbbf24]/15 border border-[#fbbf24]/30 mb-5">
                  <Film className="w-7 h-7 text-[#fbbf24]" />
                </div>
                <h3 className="text-2xl md:text-3xl font-black mb-2">An open canvas</h3>
                <p className="text-gray-400 mb-6 leading-relaxed">
                  No films submitted yet for this region. Be the first to claim the spotlight — submit your short or feature and let the audience vote.
                </p>
                <Link
                  to="/festival/submit"
                  data-testid="festival-empty-cta"
                  className="btn-red px-6 py-2.5 rounded font-semibold inline-flex items-center gap-2"
                >
                  Submit your film
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {rest.map((f) => (
                <Link
                  key={f.submission_id}
                  data-testid={`festival-film-${f.submission_id}`}
                  to={`/festival/films/${f.submission_id}`}
                  className="group relative aspect-[2/3] rounded overflow-hidden bg-[#141414] hover:scale-[1.04] transition-transform"
                >
                  {f.poster_url ? (
                    <img src={f.poster_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-600">
                      <Film className="w-10 h-10" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">{f.region || "—"}</div>
                    <h3 className="text-sm font-bold leading-tight line-clamp-2">{f.title}</h3>
                    <div className="flex items-center gap-1 text-[11px] text-gray-300 mt-1">
                      <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                      <span className="font-bold">{(f.score || 0).toFixed(2)}</span>
                      <span className="text-gray-500">· {f.total_votes || 0}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
